import {
  Box, Button, Card, CardBody, CardHeader, Heading, HStack, Icon,
  SimpleGrid, Text, VStack, Badge, Tabs, TabList, TabPanels, Tab, TabPanel, Select,
} from '@chakra-ui/react';
import { FiArrowLeft, FiDownload, FiTrash2, FiActivity, FiGitBranch } from 'react-icons/fi';
import { PageContainer } from '@/client/components/layout/PageContainer';
import { LoadingSpinner } from '@/design-system/components/LoadingSpinner';
import { ConfirmDialog } from '@/design-system/components/ConfirmDialog';
import { TopFunctionsTable, AlertsBadge, FlameGraph, ProfileCompare } from '../components';
import { ProfileStatus } from '@/lib/sdk/profiling/types';
import { useProfileDetailPage } from '../hooks';

function formatBytes(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(2)} KB`;
  return `${bytes} B`;
}

export function ProfileDetailPage() {
  const p = useProfileDetailPage();

  if (!p.isSuperAdmin) {
    return (
      <PageContainer title="Access Denied">
        <Box textAlign="center" py={12}>
          <Icon as={FiActivity} boxSize={16} color="red.500" mb={4} />
          <Text color="text-primary">Super Admin Access Required</Text>
        </Box>
      </PageContainer>
    );
  }

  if (p.isLoadingProfile) return <LoadingSpinner message="Loading profile..." />;

  if (!p.profile) {
    return (
      <PageContainer title="Profile Not Found">
        <Box textAlign="center" py={12}>
          <Text color="text-muted" mb={4}>The requested profile could not be found.</Text>
          <Button onClick={() => p.navigate(`/dashboard/${p.orgId}/profiling/profiles`)}>Back to Profiles</Button>
        </Box>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={`${p.profile.profile_type.toUpperCase()} Profile`}
      description={p.profile.description || `Collected on ${new Date(p.profile.created_at).toLocaleDateString()}`}
      action={
        <HStack spacing={3}>
          <Button leftIcon={<FiArrowLeft />} variant="outline" onClick={() => p.navigate(`/dashboard/${p.orgId}/profiling/profiles`)} borderColor="border-default" _hover={{ borderColor: 'border-hover' }}>Back</Button>
          {p.profile.download_url && (
            <Button as="a" href={p.profile.download_url} target="_blank" leftIcon={<FiDownload />} variant="outline" borderColor="border-default" _hover={{ borderColor: 'border-hover', textDecoration: 'none' }}>Download</Button>
          )}
          <Button leftIcon={<FiTrash2 />} colorScheme="red" variant="outline" onClick={p.onDeleteOpen}>Delete</Button>
        </HStack>
      }
    >
      <VStack spacing={6} align="stretch">
        <SimpleGrid columns={{ base: 1, md: 4 }} spacing={4}>
          {[
            { label: 'Type', content: <Badge colorScheme="purple" fontSize="md">{p.profile.profile_type.toUpperCase()}</Badge> },
            { label: 'Status', content: <Badge colorScheme={p.profile.status === ProfileStatus.COMPLETED ? 'green' : p.profile.status === ProfileStatus.COLLECTING ? 'blue' : p.profile.status === ProfileStatus.FAILED ? 'red' : 'gray'} fontSize="md">{p.profile.status}</Badge> },
            { label: 'Duration', content: <Text color="text-primary" fontWeight="bold">{p.profile.duration_seconds} seconds</Text> },
            { label: 'Size', content: <Text color="text-primary" fontWeight="bold">{formatBytes(p.profile.file_size_bytes)}</Text> },
          ].map(({ label, content }) => (
            <Card key={label} variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
              <CardBody><VStack align="start" spacing={1}><Text color="text-secondary" fontSize="sm">{label}</Text>{content}</VStack></CardBody>
            </Card>
          ))}
        </SimpleGrid>

        <Tabs colorScheme="brand">
          <TabList>
            <Tab>Analysis</Tab>
            <Tab>Flame Graph</Tab>
            <Tab>Compare</Tab>
          </TabList>
          <TabPanels>
            <TabPanel px={0}>
              {p.profile.status !== ProfileStatus.COMPLETED ? (
                <Box textAlign="center" py={8}><Text color="text-muted">Analysis is only available for completed profiles.</Text></Box>
              ) : p.isLoadingAnalysis ? <LoadingSpinner /> : (
                <VStack spacing={6} align="stretch">
                  <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
                    <CardHeader><Heading size="sm" color="text-primary">Alerts</Heading></CardHeader>
                    <CardBody pt={0}><AlertsBadge alerts={p.analysis?.alerts || []} /></CardBody>
                  </Card>
                  {p.analysis?.insights && p.analysis.insights.length > 0 && (
                    <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
                      <CardHeader><Heading size="sm" color="text-primary">Insights</Heading></CardHeader>
                      <CardBody pt={0}><VStack align="start" spacing={2}>{p.analysis.insights.map((insight, i) => <Text key={i} color="text-primary" fontSize="sm">{insight}</Text>)}</VStack></CardBody>
                    </Card>
                  )}
                  <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
                    <CardHeader><Heading size="sm" color="text-primary">Top Functions</Heading></CardHeader>
                    <CardBody pt={0}><TopFunctionsTable functions={p.analysis?.top_functions || []} /></CardBody>
                  </Card>
                  {p.analysis?.hotspots && p.analysis.hotspots.length > 0 && (
                    <Card variant="outline" bg="bg-surface" bgGradient={p.cardGradient}>
                      <CardHeader><Heading size="sm" color="text-primary">Hotspots</Heading></CardHeader>
                      <CardBody pt={0}><VStack align="start" spacing={2}>{p.analysis.hotspots.map((hotspot, i) => <Text key={i} color="text-primary" fontSize="sm" fontFamily="mono">{hotspot}</Text>)}</VStack></CardBody>
                    </Card>
                  )}
                </VStack>
              )}
            </TabPanel>

            <TabPanel px={0}>
              {p.profile.status !== ProfileStatus.COMPLETED ? (
                <Box textAlign="center" py={8}><Text color="text-muted">Flame graph is only available for completed profiles.</Text></Box>
              ) : <FlameGraph profile={p.profile} />}
            </TabPanel>

            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                <HStack spacing={4}>
                  <Select placeholder="Select profile to compare" value={p.compareProfileId} onChange={(e) => p.setCompareProfileId(e.target.value)} maxW="400px" bg="bg-surface" borderColor="border-default">
                    {p.compareableProfiles.map((cp) => (
                      <option key={cp.id} value={cp.id}>{new Date(cp.created_at).toLocaleDateString()} - {cp.description || 'No description'}</option>
                    ))}
                  </Select>
                  <Button leftIcon={<FiGitBranch />} colorScheme="brand" onClick={p.handleCompare} isDisabled={!p.compareProfileId} isLoading={p.isComparing}>Compare</Button>
                </HStack>
                {p.compareableProfiles.length === 0 && (
                  <Box textAlign="center" py={8}><Text color="text-muted">No other {p.profile.profile_type.toUpperCase()} profiles available for comparison.</Text></Box>
                )}
                <ProfileCompare comparison={p.comparisonData} isLoading={p.isComparing} />
              </VStack>
            </TabPanel>
          </TabPanels>
        </Tabs>
      </VStack>

      <ConfirmDialog
        isOpen={p.isDeleteOpen} onClose={p.onDeleteClose} onConfirm={p.handleDelete}
        title="Delete Profile" message="Are you sure you want to delete this profile? This action cannot be undone."
        confirmText="Delete" isLoading={p.isDeleting}
      />
    </PageContainer>
  );
}
